import React, { useMemo } from "react";
import {
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Wallet,
  BarChart3,
  User,
} from "lucide-react";
import { useGetMyFinanceSummaryQuery } from "../api/billingApi";
import { useStartInvoiceItemPaymentMutation } from "../api/receiptApi";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../../../utils/formatters";
function StatCard({
  title,
  value,
  icon: Icon,
}: {
  title: string;
  value: string;
  icon: any;
}) {
  return (
    <div className="bg-white border rounded-[24px] p-6 shadow-sm">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1">
            {title}
          </div>
          <div className="text-2xl font-black text-gray-900">{value}</div>
        </div>

        <div className="p-2 bg-gray-100 rounded-xl">
          <Icon className="w-5 h-5 text-gray-600" />
        </div>
      </div>
    </div>
  );
}

export default function FinancialStatusPage() {
  const { data, isLoading } = useGetMyFinanceSummaryQuery();

  console.log(data, "this is the main data**********");
  const [startPayment, { isLoading: isPaying }] =
    useStartInvoiceItemPaymentMutation();

  const navigate = useNavigate();

  const totalExpected = data?.grand_summary?.total_expected ?? 0;
  const totalPaid = data?.grand_summary?.total_paid ?? 0;
  const totalOwed = data?.grand_summary?.total_owed ?? 0;
  const progress = useMemo(() => {
    if (totalExpected === 0) return 0;

    const value = totalPaid * (100 / totalExpected);
    return Number(value.toFixed(2));
  }, [totalExpected, totalPaid]);

  const status = totalOwed === 0 ? "All Payments Cleared" : "Percentage Paid";

  const oneTimeCharges = data?.one_time?.charges ?? [];
  const annualCharges = data?.annual ?? [];

  console.log(annualCharges, "anual chargess&&&&&&&&&&&&&&&&&&&&");

  if (isLoading) {
    return <div className="p-10">Loading financial status...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* HEADER */}
      <div className="bg-white border rounded-[32px] p-6 shadow-sm flex justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gray-900 rounded-2xl">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>

          <div>
            <h1 className="text-2xl font-black">Financial Status</h1>
            <p className="text-sm text-gray-500">
              Overview of your financial standing
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <User className="w-5 h-5 text-gray-500" />
          <div className="text-right">
            <div className="font-bold">{data?.user?.name}</div>
            <div className="text-sm text-gray-500">{data?.user?.category}</div>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-3 gap-6">
        <StatCard
          title="Total Expected"
          value={formatCurrency(totalExpected)}
          icon={CreditCard}
        />

        <StatCard
          title="Total Paid"
          value={formatCurrency(totalPaid)}
          icon={CheckCircle2}
        />

        <StatCard
          title="Outstanding Balance"
          value={formatCurrency(totalOwed)}
          icon={Wallet}
        />
      </div>

      {/* STATUS */}
      <div className="bg-white border rounded-[32px] p-6 space-y-4">
        <div className="flex items-center gap-3">
          {totalOwed === 0 ? (
            <CheckCircle2 className="text-green-500 w-6 h-6" />
          ) : (
            <AlertCircle className="text-yellow-500 w-6 h-6" />
          )}

          <span className="font-bold text-lg">{status}</span>
        </div>

        <div className="w-full bg-gray-200 h-3 rounded-full overflow-hidden">
          <div
            style={{ width: `${progress}%` }}
            className="bg-green-500 h-full"
          />
        </div>

        <div className="text-sm text-gray-500">
          Payment Progress: {progress}%
        </div>
      </div>

      {/* ONE TIME CHARGES */}
      <div className="bg-white border rounded-[32px] overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 font-bold">One Time Charges</div>

        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="px-6 py-4 text-left">Charge</th>
              <th className="px-6 py-4">Amount</th>
              <th className="px-6 py-4">Paid</th>
              <th className="px-6 py-4">Balance</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>

          <tbody>
            {oneTimeCharges.map((charge: any) => (
              <tr key={charge.invoice_item_id} className="border-t">
                <td className="px-6 py-4 font-semibold">
                  {charge.charge_name}

                  {/* ✅ YEAR */}
                  <div className="text-xs text-gray-400 mt-1">
                    Year: {charge.year ?? "—"}
                  </div>

                  {charge.is_partial && (
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                      Partial
                    </span>
                  )}
                </td>

                <td className="px-6 py-4">{formatCurrency(charge.amount)}</td>

                <td className="px-6 py-4 text-green-600 font-semibold">
                  {formatCurrency(charge.paid)}
                </td>

                <td className="px-6 py-4 font-bold">
                  {formatCurrency(charge.balance)}
                </td>

                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        charge.status === "paid"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {charge.status}
                    </span>

                    <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                      {charge.invoice_status}
                    </span>
                  </div>
                </td>

                {/* ✅ PAY FLOW */}
                <td className="px-6 py-4 text-right">
                  {charge.balance > 0 && (
                    <button
                      onClick={() =>
                        navigate(
                          `/invoice/${charge.invoice_id}/pay?year=${charge.year}&amount=${charge.balance}`
                        )
                      }
                      className="bg-black text-white px-4 py-2 rounded-lg text-xs font-semibold hover:opacity-90"
                    >
                      Pay Balance
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ANNUAL CHARGES */}
      {annualCharges.map((yearGroup: any) => (
        <div
          key={yearGroup.year}
          className="bg-white border border-gray-100 rounded-[2rem] overflow-hidden shadow-sm mb-8"
        >
          {/* --- Table Header --- */}
          <div className="px-8 py-5 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-black uppercase tracking-[0.15em] text-gray-900">
                Billing Period:{" "}
                {yearGroup.year === "unknown"
                  ? yearGroup.charges[0]?.year
                  : yearGroup.year}
              </span>
              {yearGroup.has_partial && (
                <span className="text-[10px] font-black uppercase tracking-widest bg-amber-100 text-amber-700 px-3 py-1 rounded-full border border-amber-200">
                  Partial Credit Found
                </span>
              )}
            </div>

            <div className="text-right">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Total Owed
              </p>
              <p className="text-lg font-black text-gray-900">
                {formatCurrency(yearGroup.total_owed)}
              </p>
            </div>
          </div>

          {/* --- Matrix Table --- */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-50">
                  <th className="px-8 py-4 text-left">Description</th>
                  <th className="px-6 py-4 text-right">Invoice Amount</th>
                  <th className="px-6 py-4 text-right text-emerald-600">
                    Paid to Date
                  </th>
                  <th className="px-6 py-4 text-right">Balance</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-8 py-4 text-right">Remittance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {yearGroup.charges.map((charge: any) => (
                  <tr
                    key={charge.invoice_item_id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-8 py-6">
                      <div className="font-black text-gray-900 uppercase tracking-tight text-sm">
                        {charge.charge_name}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full" />
                        ID: {charge.invoice_item_id.substring(0, 8)}...
                      </div>
                    </td>

                    <td className="px-6 py-4 text-right font-mono font-bold text-gray-600">
                      {formatCurrency(charge.amount)}
                    </td>

                    <td className="px-6 py-4 text-right font-mono font-bold text-emerald-600">
                      {formatCurrency(charge.paid)}
                    </td>

                    <td className="px-6 py-4 text-right font-mono font-black text-gray-900">
                      {formatCurrency(charge.balance)}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span
                        className={`
                  text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border
                  ${
                    charge.status === "paid"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                      : charge.status === "partial"
                      ? "bg-amber-50 text-amber-700 border-amber-100"
                      : "bg-gray-50 text-gray-500 border-gray-100"
                  }
                `}
                      >
                        {charge.status}
                      </span>
                    </td>

                    <td className="px-8 py-4 text-right">
                      {charge.balance > 0 ? (
                        <button
                          disabled={isPaying}
                          onClick={async () => {
                            try {
                              const data = await startPayment(
                                charge.invoice_item_id
                              ).unwrap();
                              const form = document.createElement("form");
                              form.method = "POST";
                              form.action = data.payment_url;
                              Object.entries(data).forEach(([key, value]) => {
                                if (key === "payment_url") return;
                                const input = document.createElement("input");
                                input.type = "hidden";
                                input.name = key;
                                input.value = String(value);
                                form.appendChild(input);
                              });
                              document.body.appendChild(form);
                              form.submit();
                            } catch (err) {
                              console.error(err);
                              alert("Payment initiation failed.");
                            }
                          }}
                          className="inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-gray-200"
                        >
                          {isPaying ? "Processing..." : "Pay Balance"}
                        </button>
                      ) : (
                        <div className="flex items-center justify-end text-emerald-500 gap-1.5">
                          <span className="text-[10px] font-black uppercase tracking-widest">
                            Cleared
                          </span>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="3"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
